import { ExpandLess, ExpandMore } from "@mui/icons-material";
import { Box, Collapse, Divider, IconButton, Typography } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { useCallback, useEffect, useState } from "react";
import useAPI from "../../../../api/hooks/useAPI";
import useColors from "../../../../api/hooks/useColors";
import { resetFiltersToDefaults } from "../../../../components/filters/ClearFiltersButton";
import EditFilterModal from "../../../../components/filters/saved/EditFilterModal";
import SavedFiltersRow from "../../../../components/filters/saved/SavedFiltersRow";
import { ComplexEntityTypes } from "../../../../utils/constants";
import { deepEqual } from "../../../../utils/utils";
import EntityTypeRegularFilters from "./EntityTypeRegularFilters";
import EntityTypeSmartFilters from "./smartFilters/EntityTypeSmartFilters";

export default function FiltersBox({
  entityType,
  fetchedEntities,
  assets,
  appliedFilters,
  setAppliedFilters,
  filterAndOrLogics,
  setFilterAndOrLogics,
  allLabels,
  allCustomTags,
}) {
  const colors = useColors();
  const {
    trackEvent,
    saveFilters,
    loadFilters,
    deleteSavedFilter,
    updateSavedFilter,
  } = useAPI();

  const [isExpanded, setIsExpanded] = useState(true);

  const [savedFilters, setSavedFilters] = useState([]);
  const [activeFilterId, setActiveFilterId] = useState(null);
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingFilter, setEditingFilter] = useState(null);
  const [isSavingFilter, setIsSavingFilter] = useState(false);

  // Load saved filters for entity type page
  const { data: loadedSavedFilters } = useQuery({
    queryKey: ["savedFilters", `entity_${entityType}`],
    queryFn: () => loadFilters(`entity_${entityType}`),
    select: (data) => data.data.filters,
  });

  // Reset active filter if applied filters don't match the active saved filter
  useEffect(() => {
    if (activeFilterId && savedFilters.length > 0) {
      const activeFilter = savedFilters.find((f) => f.id === activeFilterId);
      if (activeFilter) {
        // Compare current applied filters with saved filter's filters
        const filtersMatch = deepEqual(appliedFilters, activeFilter.filters);
        if (!filtersMatch) {
          setActiveFilterId(null);
        }
      }
    }
  }, [appliedFilters, activeFilterId, savedFilters]);

  // Saved filters handlers
  const handleSaveFilter = async (filterData) => {
    setIsSavingFilter(true);
    try {
      const filterId = `filter_${Date.now()}`;
      const savedFilter = {
        id: filterId,
        name: filterData.name,
        description: filterData.description,
        filters: { ...appliedFilters },
        is_default: filterData.is_default,
      };

      await saveFilters(`entity_${entityType}`, savedFilter);

      // Refresh saved filters
      const response = await loadFilters(`entity_${entityType}`);
      setSavedFilters(response.data.filters || []);

      if (filterData.is_default) {
        setActiveFilterId(filterId);
      }

      setShowSaveModal(false);
      trackEvent(`User saved a filter in ${entityType} entities page`);
    } catch (error) {
      console.error("Error saving filter:", error);
    } finally {
      setIsSavingFilter(false);
    }
  };

  const handleApplyFilter = useCallback(
    (filter) => {
      setAppliedFilters(filter.filters);
      setActiveFilterId(filter.id);
      trackEvent(`User applied saved filter in ${entityType} entities page`);
    },
    [entityType, trackEvent, setAppliedFilters, setActiveFilterId]
  );

  // Handle loaded saved filters
  useEffect(() => {
    if (loadedSavedFilters) {
      setSavedFilters(loadedSavedFilters || []);
      // Apply default filter if exists and no other filters are applied
      const defaultFilter = loadedSavedFilters?.find((f) => f.is_default);
      if (defaultFilter && !activeFilterId) {
        handleApplyFilter(defaultFilter);
      }
    }
  }, [loadedSavedFilters, activeFilterId, handleApplyFilter]);

  const handleEditFilter = (filter) => {
    setEditingFilter(filter);
    setShowEditModal(true);
  };

  const handleUpdateFilter = async (updateData) => {
    if (!editingFilter) return;

    setIsSavingFilter(true);
    try {
      await updateSavedFilter(
        `entity_${entityType}`,
        editingFilter.id,
        updateData
      );

      // Refresh saved filters
      const response = await loadFilters(`entity_${entityType}`);
      setSavedFilters(response.data.filters || []);

      setShowEditModal(false);
      setEditingFilter(null);
      trackEvent(`User updated saved filter in ${entityType} entities page`);
    } catch (error) {
      console.error("Error updating filter:", error);
    } finally {
      setIsSavingFilter(false);
    }
  };

  const handleDeleteFilter = async (filter) => {
    try {
      await deleteSavedFilter(`entity_${entityType}`, filter.id);

      // Refresh saved filters
      const response = await loadFilters(`entity_${entityType}`);
      setSavedFilters(response.data.filters || []);

      if (activeFilterId === filter.id) {
        setActiveFilterId(null);
      }

      trackEvent(`User deleted saved filter in ${entityType} entities page`);
    } catch (error) {
      console.error("Error deleting filter:", error);
    }
  };

  const activeFiltersCount = Object.values(appliedFilters || {}).filter(
    (val) => {
      if (val == null) return false;
      if (Array.isArray(val) || typeof val === "string") return val.length > 0;
      if (typeof val === "object") return Object.keys(val).length > 0;
      return !!val;
    }
  ).length;

  return (
    <>
      <Box
        width="100%"
        padding="16px"
        display="flex"
        flexDirection="column"
        gap={2}
        sx={{
          borderRadius: "12px",
          backgroundColor: colors.grey_2percent,
          boxShadow: colors.entity_type_overview_top_shadow,
        }}
      >
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          onClick={() => setIsExpanded(!isExpanded)}
          sx={{ cursor: "pointer", userSelect: "none" }}
        >
          <Box display="flex" alignItems="center" gap={1}>
            <Typography
              variant="h6"
              fontWeight={600}
              color={colors.text_primary}
            >
              Filters
            </Typography>
            {!isExpanded && activeFiltersCount > 0 && (
              <Typography variant="body2" color={colors.text_secondary}>
                ({activeFiltersCount} active)
              </Typography>
            )}
          </Box>
          <IconButton size="small" sx={{ color: colors.text_secondary }}>
            {isExpanded ? <ExpandLess /> : <ExpandMore />}
          </IconButton>
        </Box>

        <Collapse in={isExpanded} timeout="auto">
          <Box display="flex" flexDirection="column" gap={2}>
            {savedFilters && savedFilters.length > 0 && (
              <>
                <SavedFiltersRow
                  savedFilters={savedFilters}
                  onApplyFilter={handleApplyFilter}
                  onEditFilter={handleEditFilter}
                  onDeleteFilter={handleDeleteFilter}
                  onUnselectFilter={() => {
                    resetFiltersToDefaults(appliedFilters, setAppliedFilters);
                    setActiveFilterId(null);
                    trackEvent(
                      `User unselected saved filter in ${entityType} entities page`
                    );
                  }}
                  activeFilterId={activeFilterId}
                />
                <Divider />
              </>
            )}

            {/* TODO - Remove the condition when implementing smart filters for App entity type */}
            {/* https://linear.app/breeze-security/issue/DEV-2682/entities-smart-filters-follow-ups */}
            {entityType !== ComplexEntityTypes.APP && (
              <>
                <EntityTypeSmartFilters
                  entityType={entityType}
                  fetchedEntities={fetchedEntities}
                  appliedFilters={appliedFilters}
                  setAppliedFilters={setAppliedFilters}
                  filterAndOrLogics={filterAndOrLogics}
                  setFilterAndOrLogics={setFilterAndOrLogics}
                />
                <Divider />
              </>
            )}
            <Box display="flex" alignItems="center" gap={2}>
              <Box flex={1}>
                <EntityTypeRegularFilters
                  entityType={entityType}
                  fetchedEntities={fetchedEntities}
                  assets={assets}
                  appliedFilters={appliedFilters}
                  setAppliedFilters={setAppliedFilters}
                  filterAndOrLogics={filterAndOrLogics}
                  setFilterAndOrLogics={setFilterAndOrLogics}
                  allLabels={allLabels}
                  allCustomTags={allCustomTags}
                />
              </Box>
            </Box>
          </Box>
        </Collapse>
      </Box>

      {/* Edit Filter Modal */}
      <EditFilterModal
        open={showEditModal}
        onClose={() => {
          setShowEditModal(false);
          setEditingFilter(null);
        }}
        onSave={handleUpdateFilter}
        filter={editingFilter}
        isLoading={isSavingFilter}
      />
    </>
  );
}
